/**
 * 等待态主体 —— 出图途中这一页放什么(W2,2026-08-02)。
 *
 * ═══ 它替掉的是什么 ═══════════════════════════════════════════════════════════
 * 改前等待态**没有主体**:`AssetEditor` 要 `selectedAsset` 才渲染,而等待态没有产物,
 * 于是成品页最大的那块版面在等待时是一片纯背景,页面 2/3 空着,视觉重心全压在顶部一条
 * 横向骨架上 —— 那条还得左右滑。等待态不是被设计出来的,是成品页把内容抽掉剩下的。
 *
 * ═══ 两个阶段,同一套槽 ═══════════════════════════════════════════════════════
 *   QUEUED    一张都没出 → 大格目标网格,**占住成品页大图的位置**
 *   STREAMING 已出 ≥1 张 → 最新一张升主位 + 右侧纵向槽位列
 * 阶段切换是全页**唯一**一次布局变化;槽本身自始至终不动,只是内容原地换。
 *
 * ★网格用 `auto-fill` + 按本单最宽画幅算的列宽,**永远不横滚**:
 *   左右方向不表示时间也不表示权重,横滑本身不携带信息(用户原话「从左往右的无意义滑动」)。
 */
import { useMemo, useState } from "react";

import { cn } from "@/shared/lib/utils";
import { ProgressBand } from "./ProgressBand";
import { SlotFrame, maxSlotAspect } from "./SlotFrame";
import type { GenSlot, GenerationProgress } from "../../../model/generationProgress";

/** 阶段一格子高度 —— 这块地方本来就是给大图的,不是缩略图。 */
const STAGE_BIG = 208;
/** 主位高度。 */
const STAGE_HERO = 340;
/** 右列缩略高度。 */
const STAGE_THUMB = 34;

function SlotCaption({ slot }: { slot: GenSlot }) {
  return (
    <span className="min-w-0 block">
      <span className="block truncate text-[12.5px] font-medium text-foreground">
        {slot.seq}. {slot.label}
      </span>
      {slot.sub && (
        <span className="block truncate text-2xs text-muted-foreground">{slot.sub}</span>
      )}
    </span>
  );
}

/** 目标清单网格 —— 「这一单将来会有什么」提前铺满。 */
function TargetGrid({
  slots,
  stageH,
  selectedSeq,
  onPick,
}: {
  slots: GenSlot[];
  stageH: number;
  selectedSeq?: number;
  onPick?: (slot: GenSlot) => void;
}) {
  const colMin = useMemo(() => Math.round(stageH * maxSlotAspect(slots)) + 16, [stageH, slots]);
  return (
    <ul
      className="grid gap-3"
      /* ★minmax 里夹一层 min(...,100%):窄屏下列宽比容器还宽会把整页顶出横向滚动条 */
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${colMin}px, 100%), 1fr))` }}
    >
      {slots.map((s, i) => (
        <li key={s.seq}>
          <button
            type="button"
            disabled={s.state !== "landed"}
            onClick={() => onPick?.(s)}
            /* 进场 stagger:一次性交代「这一单有 N 格」。总时长封顶,张数多也不拖 */
            className={cn(
              "fx-slot-enter flex w-full flex-col gap-1.5 rounded-lg p-1 text-left transition-colors",
              s.state === "landed" && "cursor-pointer hover:bg-muted/40",
              selectedSeq === s.seq && "bg-primary/10"
            )}
            style={{ animationDelay: `${Math.min(i, 6) * 40}ms` }}
          >
            {/* 行高由槽自己的 aspect-ratio 决定(宽屏下恒 = stageH,窄屏跟着缩) */}
            <span className="flex items-center" style={{ minHeight: 0 }}>
              <SlotFrame slot={s} stageH={stageH} dense={stageH < 120} />
            </span>
            <SlotCaption slot={s} />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function GeneratingPanel({
  progress,
  submittedAt,
  prompt,
  selectedSeq,
  onPick,
}: {
  progress: GenerationProgress;
  submittedAt?: string | null;
  /** 用户提交的文案 —— 等着的时候正是想核对「我刚才写的是什么」的时候 */
  prompt?: string | null;
  /** 用户点过的那一槽(没点过就跟着最新落地的走) */
  selectedSeq?: number;
  onPick?: (slot: GenSlot) => void;
}) {
  /**
   * ★等待态**没有**「重出没出来的 N 张」按钮。
   *   第一版我加了一个,e2e 截图上看着挺合理,查了后端才发现两处都是错的:
   *     ① `POST /tasks/{id}/retry` 是 `resetForRetry` —— **清空已有产物后整单重跑**,
   *        不是补出失败的那几张。按「重出没出来的 1 张」去点,会把已经出好并扣过费的图抹掉;
   *     ② 它只放行 FAILED/PARTIAL/CANCELLED,RUNNING 下直接 400。
   *   也就是说这个按钮既说了假话又点不动 —— 正是本批要清掉的那类东西。
   *   失败槽在终态由既有的「整体重试」/ 组图单张重出接手。
   */
  const { slots, done, failed, total, phase } = progress;
  const [hovered, setHovered] = useState<number | null>(null);

  // 主位 = 用户选中的那槽;没选过就用**最新**落地的一张(先出的先看得到)
  const latest = [...slots].reverse().find((s) => s.state === "landed");
  const hero = slots.find((s) => s.seq === selectedSeq && s.state === "landed") ?? latest;

  /**
   * 文案回显 —— 摊开显示,不折起来。
   * 页尾的「本次设置」折叠区默认收起,而等待的这几十秒恰恰是用户想核对文案的时刻;
   * 让他为了看一眼自己写的字去点开一个折叠区,是把成品页的信息层级照搬到了等待态。
   */
  const promptBlock = prompt?.trim() ? (
    <div className="rounded-lg border border-border/70 bg-card px-3.5 py-2.5">
      <p className="text-[12px] font-semibold text-muted-foreground">这次出的是什么</p>
      <p className="mt-1 line-clamp-3 text-[12.5px] leading-relaxed text-foreground/85">
        {prompt.trim()}
      </p>
    </div>
  ) : null;

  const band = (
    <ProgressBand
      slots={slots}
      done={done}
      failed={failed}
      total={total}
      submittedAt={submittedAt}
    />
  );

  // ── 阶段一:一张都还没出 ────────────────────────────────────────────────────
  if (phase === "QUEUED" || !hero) {
    return (
      <div key="queued" className="fx-phase-swap space-y-4">
        {band}
        <TargetGrid slots={slots} stageH={STAGE_BIG} onPick={onPick} />
        {promptBlock}
      </div>
    );
  }

  // ── 阶段二:边出边看 ────────────────────────────────────────────────────────
  //   ★key 换新 → 整块重新挂载 → `.fx-phase-swap` 只在**相位翻转那一次**跑,
  //     后续每张产物到货不会重放(那会变成每 5 秒闪一下)。
  return (
    <div key="streaming" className="fx-phase-swap space-y-4">
      {band}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-2">
          <div
            className="flex items-center justify-center rounded-xl border border-border/60 bg-muted/20 p-4"
            style={{ minHeight: 120 }}
          >
            <SlotFrame slot={hero} stageH={STAGE_HERO} />
          </div>
          <p className="text-center text-[13px]">
            <span className="font-semibold text-foreground">
              {hero.seq}. {hero.label}
            </span>
            {hero.sub && <span className="ml-2 text-muted-foreground">{hero.sub}</span>}
          </p>
        </div>

        <aside className="space-y-3">
          <ul className="space-y-1" aria-label="这一单的全部产出">
            {slots.map((s) => (
              <li key={s.seq}>
                <button
                  type="button"
                  disabled={s.state !== "landed"}
                  onClick={() => onPick?.(s)}
                  onMouseEnter={() => setHovered(s.seq)}
                  onMouseLeave={() => setHovered(null)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors",
                    s.seq === hero.seq && "bg-primary/10",
                    s.state === "landed" &&
                      s.seq !== hero.seq &&
                      hovered === s.seq &&
                      "bg-muted/50",
                    s.state === "landed" && "cursor-pointer"
                  )}
                >
                  <span className="flex h-9 w-[52px] shrink-0 items-center justify-center">
                    <SlotFrame slot={s} stageH={STAGE_THUMB} dense />
                  </span>
                  <SlotCaption slot={s} />
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>
      {promptBlock}
    </div>
  );
}
